import type { CurrencyPrimitive } from "@/api/cashier";
import { useFormatAmount } from "@/hooks/useFormatAmount";
import { getDecimalSeparator, sanitizeNumberInput } from "@/utils/helpers";
import { useState, useEffect } from "react";
import style from "./AmountInput.module.css";

type AmountInputProps = {
  value: string;
  onChange?: (amount: string) => void;
  disabled?: boolean;
  currency: CurrencyPrimitive | undefined;
  placeholder: string;
  testId?: string;
  onFocus?: () => void;
  onBlur?: () => void;
  withFormatting?: boolean;
  maxLength?: number;
  locale: string;
};

export const AmountInput = ({
  value,
  onChange,
  disabled = false,
  currency,
  placeholder,
  testId,
  onFocus,
  onBlur,
  withFormatting = true,
  maxLength,
  locale,
}: AmountInputProps) => {
  const { formatSanitizedInput } = useFormatAmount();
  const [displayValue, setDisplayValue] = useState(value);

  const decimalSep = getDecimalSeparator(locale);

  useEffect(() => {
    if (value === "" || value === undefined) {
      setDisplayValue("");
      return;
    }
    if (!withFormatting) {
      setDisplayValue(value);
      return;
    }

    // Don't overwrite display if it already represents the same normalized value
    // needed when we delete the last decimal place
    const { numeric: currentNormalized } = sanitizeNumberInput(
      displayValue,
      currency,
      locale
    );
    if (currentNormalized === value) return;

    // value is always stored normalized with "." — convert to locale form before sanitizing
    const localeValue =
      decimalSep !== "." ? value.replace(".", decimalSep) : value;
    const { sanitized, numeric } = sanitizeNumberInput(
      localeValue,
      currency,
      locale
    );
    if (sanitized) {
      setDisplayValue(
        formatSanitizedInput(numeric, sanitized, currency, locale)
      );
    }
  }, [
    value,
    currency,
    locale,
    withFormatting,
    decimalSep,
    formatSanitizedInput,
    displayValue,
  ]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;

    // Allow clearing the input completely
    if (input === "") {
      setDisplayValue("");
      onChange?.("");
      return;
    }

    if (!withFormatting) {
      setDisplayValue(input);
      onChange?.(input);
      return;
    }

    const { sanitized, numeric } = sanitizeNumberInput(input, currency, locale);

    if (sanitized) {
      const formatted = formatSanitizedInput(
        numeric,
        sanitized,
        currency,
        locale
      );
      setDisplayValue(formatted);
      // onChange always receives normalized value
      onChange?.(numeric ?? "");
    }
  };

  return (
    <input
      type="text"
      inputMode="decimal"
      value={displayValue}
      onChange={handleChange}
      placeholder={placeholder}
      className={style["amount-bottom"]}
      disabled={disabled}
      data-testid={testId}
      onFocus={onFocus}
      onBlur={onBlur}
      maxLength={maxLength}
    />
  );
};
